import { useCallback, useEffect } from 'react';

const MEDIUM_BOTTOM_SIZE = 65;
const DRAWER_RIGHT_OFFSET = 510;
const ZENDESK_BUTTON = 'launcher';
const ZENDESK_WEB_WIDGET = 'webWidget';

const zendeskBottomClass = (zendeskBottomValue: number) =>
  `zendesk_bottom_${zendeskBottomValue}`;

const zendeskRightClass = (zendeskRightValue: number) =>
  `zendesk_right_${zendeskRightValue}`;

const zendeskInvisibleClass = 'zendesk_invisible';

type Props = {
  moveZendesk: boolean;
  raise?: boolean;
  hide?: boolean;
  drawerOpen?: boolean;
  forceReset?: boolean;
  rightOffset?: number;
};

export const useMoveZendesk = ({
  moveZendesk,
  raise = false,
  hide = false,
  drawerOpen = false,
  forceReset = false,
  rightOffset = DRAWER_RIGHT_OFFSET,
}: Props) => {
  const applyClasses = useCallback(() => {
    const launcher = document.getElementById(ZENDESK_BUTTON);
    const webWidget = document.getElementById(ZENDESK_WEB_WIDGET);
    const iframeList = [launcher, webWidget].filter(Boolean) as HTMLElement[];

    if (forceReset) {
      iframeList.forEach((iframe) => {
        iframe.classList.remove(
          zendeskBottomClass(MEDIUM_BOTTOM_SIZE),
          zendeskRightClass(rightOffset),
          zendeskInvisibleClass
        );
      });

      return;
    }

    iframeList.forEach((iframe) => {
      iframe.classList.remove(
        zendeskBottomClass(MEDIUM_BOTTOM_SIZE),
        zendeskRightClass(rightOffset),
        zendeskInvisibleClass
      );
    });

    if (raise) {
      iframeList.forEach((iframe) =>
        iframe.classList.add(zendeskBottomClass(MEDIUM_BOTTOM_SIZE))
      );
    }

    if (hide) {
      iframeList.forEach((iframe) =>
        iframe.classList.add(zendeskInvisibleClass)
      );
    }

    if (drawerOpen) {
      iframeList.forEach((iframe) =>
        iframe.classList.add(zendeskRightClass(rightOffset))
      );
    }
  }, [forceReset, raise, hide, drawerOpen, rightOffset]);

  useEffect(() => {
    if (!moveZendesk) {
      return;
    }

    applyClasses();

    const observer = new MutationObserver(() => {
      applyClasses();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();

      const launcher = document.getElementById(ZENDESK_BUTTON);
      const webWidget = document.getElementById(ZENDESK_WEB_WIDGET);

      [launcher, webWidget].forEach((iframe) => {
        if (iframe) {
          iframe.classList.remove(
            zendeskBottomClass(MEDIUM_BOTTOM_SIZE),
            zendeskRightClass(rightOffset),
            zendeskInvisibleClass
          );
        }
      });
    };
  }, [applyClasses, moveZendesk, rightOffset]);
};
